import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Button, ExpandableSection, HelperText, HelperTextItem, Label, LabelGroup, Stack, StackItem, TextArea, TextInput } from '@patternfly/react-core';

import { Group, parseGroupList } from './parseGroups';
import { Fetcher } from './types';

// ---------------------------------------------------------------------------
// AD group picker: live Graph search, with configured groups as suggestions and manual id entry as a fallback
// ---------------------------------------------------------------------------
export const GroupPicker: React.FC<{ value: Group[]; onChange: (groups: Group[]) => void; api: Fetcher; suggestions: Group[]; mine: Group[] }> = ({
  value,
  onChange,
  api,
  suggestions,
  mine,
}) => {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<Group[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteResult, setPasteResult] = useState<string | null>(null);

  useEffect(() => {
    if (term.trim().length < 2) {
      setResults([]);
      setSearchError(null);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const res = await api<{ groups: Group[] }>(`/api/app-config/groups?search=${encodeURIComponent(term.trim())}`);
        if (!cancelled) {
          setResults(res.groups);
          setSearchError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setResults([]);
          setSearchError(e instanceof Error ? e.message : 'Group search failed');
        }
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [term, api]);

  const selectedIds = useMemo(() => new Set(value.map(g => g.id)), [value]);
  const add = (g: Group) => {
    if (!selectedIds.has(g.id)) onChange([...value, g]);
  };
  const needle = term.trim().toLowerCase();
  const fallback = searchError ? suggestions.filter(g => !needle || g.name.toLowerCase().includes(needle)).slice(0, 15) : [];
  const options = (searchError ? fallback : results).filter(g => !selectedIds.has(g.id));
  const mineOptions = mine.filter(g => !selectedIds.has(g.id) && (!needle || g.name.toLowerCase().includes(needle))).slice(0, 15);

  const applyPaste = () => {
    const { groups, skipped } = parseGroupList(pasteText, [...suggestions, ...mine, ...results]);
    const fresh = groups.filter(g => !selectedIds.has(g.id));
    if (fresh.length > 0) onChange([...value, ...fresh]);
    const parts = [`Added ${fresh.length} group${fresh.length === 1 ? '' : 's'}`];
    if (groups.length > fresh.length) parts.push(`${groups.length - fresh.length} already selected`);
    if (skipped.length > 0) parts.push(`skipped ${skipped.length}: ${skipped.map(s => `"${s.line}" (${s.reason})`).join('; ')}`);
    setPasteResult(parts.join(', '));
    setPasteText(skipped.map(s => s.line).join('\n'));
  };

  return (
    <Stack hasGutter>
      <StackItem>
        {value.length === 0 ? (
          <em>No groups selected</em>
        ) : (
          <LabelGroup numLabels={20}>
            {value.map(g => (
              <Label key={g.id} onClose={() => onChange(value.filter(v => v.id !== g.id))} title={g.id}>
                {g.name}
              </Label>
            ))}
          </LabelGroup>
        )}
      </StackItem>
      <StackItem>
        <TextInput
          aria-label="Search AD groups"
          placeholder="Search AD groups by name (e.g. AD-ITP-Team-TDH)"
          value={term}
          onChange={(_e, v) => setTerm(v)}
        />
        {searchError && (
          <HelperText style={{ marginTop: 4 }}>
            <HelperTextItem variant="warning">{searchError} Showing configured groups instead.</HelperTextItem>
          </HelperText>
        )}
        {mineOptions.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <span style={{ opacity: 0.7 }}>Your groups: </span>
            <LabelGroup numLabels={30} style={{ display: 'inline-flex' }}>
              {mineOptions.map(g => (
                <Label key={g.id} variant="outline" color="green" onClick={() => add(g)} title={g.id}>
                  + {g.name}
                </Label>
              ))}
            </LabelGroup>
          </div>
        )}
        {options.length > 0 && (
          <LabelGroup numLabels={30} style={{ marginTop: 8 }}>
            {options.map(g => (
              <Label key={g.id} variant="outline" onClick={() => add(g)} title={g.id}>
                + {g.name}
              </Label>
            ))}
          </LabelGroup>
        )}
      </StackItem>
      <StackItem>
        <ExpandableSection toggleText="Paste a list of groups" isExpanded={pasteOpen} onToggle={(_e, open) => setPasteOpen(open)}>
          <TextArea
            aria-label="Paste groups"
            placeholder={'One group per line: <group id>, or <name>,<group id>, or a known group name'}
            value={pasteText}
            onChange={(_e, v) => setPasteText(v)}
            rows={4}
          />
          <Button variant="secondary" isDisabled={!pasteText.trim()} onClick={applyPaste} style={{ marginTop: 8 }}>
            Add pasted groups
          </Button>
          {pasteResult && (
            <HelperText style={{ marginTop: 4 }}>
              <HelperTextItem>{pasteResult}</HelperTextItem>
            </HelperText>
          )}
        </ExpandableSection>
      </StackItem>
    </Stack>
  );
};

const PATTERN_CHARS = /^[\w\-.*+?^$|[\]\\ ]+$/;

/** Mirrors the server's rules so a bad pattern is flagged before saving. */
export function patternError(pattern: string | undefined): string | null {
  const p = (pattern ?? '').trim();
  if (!p) return null;
  if (p.length > 120) return 'Too long (120 characters at most).';
  if (!PATTERN_CHARS.test(p)) return 'Only letters, digits and - . * + ? ^ $ | [ ] \\ are allowed (no parentheses).';
  try {
    new RegExp(p);
  } catch {
    return 'This is not a valid pattern.';
  }
  return null;
}

export function patternPreview(pattern: string | undefined, known: Group[]): string {
  const p = (pattern ?? '').trim();
  if (!p) return 'Matches members of any group whose name fits the pattern, in addition to the groups above.';
  const re = new RegExp(p);
  const names = [...new Set(known.filter(g => re.test(g.name)).map(g => g.name))];
  if (names.length === 0) return 'No configured or personal group matches yet (other groups may still match).';
  return `Matches ${names.length} known group${names.length === 1 ? '' : 's'}: ${names.slice(0, 6).join(', ')}${names.length > 6 ? ', ...' : ''}`;
}

